import { defineComponent, ref } from "vue";
import moment from "moment";
import { Container } from "aurelia-dependency-injection";

import { formatDateInput } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { BranchesService } from "../../../../services/branches-service";
import { AgencyService, IGetAgency, IGetOrganization } from "../../../../services/agency.service";
import { ToastrService } from "../../../../services/toastr.service";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { EnumsService } from "../../../../services/enums.service";
import { TeamsService } from "../../../../services/teams.service";
import { ProvidersService } from "../../../../services/providers-service";
import { IBranchResponse } from "../../../../resources-vue/vue-interfaces/i-branch";
import config from "../../../../common/config";
import { ApplicationEnum, ApplicationNameEnum } from "../../../../enums/ApplicationEnum";
import { FrequencyVariance } from "../../../../enums/FrequencyVarianceEnum";
import { enumToMap } from "../../../../common/vue-helpers/modifiers/enum-modifiers";
import { IMultiSelectDropDownOption } from "../../../../resources-vue/vue-interfaces/i-grouped-multiselect";
import { ParentPermissionEnum } from "../../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../../enums/PermissionActionEnum";
import { FrequencyStatuses } from "../../../../enums/FrequencyStatusesEnum";
import { ITypeaheadOptions } from "../../../../resources-vue/vue-interfaces/i-typeahead";
import { IPageCallbackResult } from "../../../../resources-vue/vue-interfaces/i-page-container";
import { IFrequencyWatchRequest } from "../../../../resources-vue/vue-interfaces/i-reports";
import { FrequencyHHDiscipline } from "../../../../enums/HHFrequencyEnum";
import { PAGE_FAIL_RESPONSE } from "../../../../resources/elements/pagination";

import MultiSelectFilter from "../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import PageLayout from "../../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import Pagination from "../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import SortableTh from "../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import FrequencyWatchHHRow from "./FrequencyWatchHHRow/FrequencyWatchHHRow.vue";
import ErrorMessageHandler from "../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import DateRangePicker from "../../../../resources-vue/vue-custom-elements/DateRangePicker/DateRangePicker.vue";
import MultiSelect from "../../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";

export default defineComponent({
    components: {
        MultiSelectFilter,
        PageLayout,
        Pagination,
        SortableTh,
        FrequencyWatchHHRow,
        ErrorMessageHandler,
        DateRangePicker,
        MultiSelect
    },

    props: {
        application: { typeof: ApplicationEnum, default: null },
        getFrequencyWatchData: { typeof: Function, default: null }
    },

    data(vm) {
        return {
            _branchService: null as BranchesService,
            _agencyService: null as AgencyService,
            _toastrService: null as ToastrService,
            _permissionManager: null as PermissionManager,
            _enumService: null as EnumsService,
            _teamService: null as TeamsService,
            _providerService: null as ProvidersService,
            dateFrom: moment().subtract(30, "days").format("MM/DD/YYYY") as string,
            dateTo: moment().add(30, "days").format("MM/DD/YYYY") as string,
            newDateRange: (moment().subtract(30, "days").format("MM/DD/YYYY") as string)
                .concat(" - ")
                .concat(moment().add(30, "days").format("MM/DD/YYYY") as string),
            branches: [] as IBranchResponse[],
            branchList: [] as IBranchResponse[],
            organizations: [] as IGetOrganization[],
            organizationId: "",
            sortBy: "patientLastName",
            sortOrder: "asc",
            agencyList: [] as IGetAgency[],
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedAgencies: [] as string[],
            selectedAgency: "" as string,
            agencyOptions: [] as any,
            branchMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedBranches: [] as string[],
            FrequencyVarianceEnums: FrequencyVariance,
            frequencyVariance: null as string,
            selectedStatuses: [] as ITypeaheadOptions[],
            freqyencyWatchHHPagination: null,
            isError: false as boolean,
            isLoading: false as boolean,
            page: null,
            columns: 14,
            pageSize: 10,
            selectedAgenciesName: [] as string[],
            selectedDiscipline: [] as ITypeaheadOptions[],
            formatDateInput,
            enumToMap
        };
    },
    created() {
        this._branchService = Container.instance.get(BranchesService);
        this._agencyService = Container.instance.get(AgencyService);
        this._toastrService = Container.instance.get(ToastrService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._enumService = Container.instance.get(EnumsService);
        this._teamService = Container.instance.get(TeamsService);
        this._providerService = Container.instance.get(ProvidersService);
        this.freqyencyWatchHHPagination = this.$refs?.freqyencyWatchHosPagination as typeof Pagination;
        this.isLoading = true;
    },
    async beforeMount() {
        this.branches = await this._branchService.getAllBranches();
        this.branchList = this.branches.filter((branch) => branch.application == this.application);
    },
    async mounted() {
        this.organizations = await this._agencyService.getOrganizations();
        this.organizationId = window.localStorage.getItem(config.organizationId);
        this.agencyList = this.organizations.find(
            (organization: { id: string }) => organization.id === this.organizationId
        ).agencies;
        this.agencyList = this.agencyList.map((agency) => {
            let agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            if (application == 256) {
                application = ApplicationEnum.AxxessHospice;
            }
            return {
                ...agency,
                application: application
            };
        });
        await this.setAgencyMultiSelect();
        await this.setBranchMultiSelect();
        this.freqyencyWatchHHPagination = this.$refs?.freqyencyWatchHHPagination as typeof Pagination;
        this.pageReset();
    },

    watch: {
        frequencyVariance(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.pageReset();
            }
        }
    },
    computed: {
        hasData() {
            return this.page?.Items.length > 0;
        },
        loadingCount() {
            return this.page?.Items.length > 0 ? this.page.Items.length : config.pageSize;
        }
    },

    methods: {
        handleDateInputChange(e: any) {
            const name = e.target.name;
            const value = e.target.value;
            if (!value.length) {
                if (name === "dateFrom") {
                    this.changeDate(this.dateFrom, name);
                } else {
                    this.changeDate(this.dateTo, name);
                }
            } else {
                this.changeDate(value, name);
            }
        },
        changeDate(date: string, name: string) {
            if (name == "dateFrom") {
                this.dateFrom = date;
            }
            if (name == "dateTo") {
                this.dateTo = date;
            }
        },
        async setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.selectedAgencies.length = 0;
                this.agencyOptions = [];
                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                let application = this.application == 256 ? ApplicationEnum.AxxessHospice : this.application;
                let allowedProducts = [application];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );
                let schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let currentapplication = item.application;
                        if (currentapplication === ApplicationEnum.AxxessHospiceFE) {
                            currentapplication = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(currentapplication);
                    })
                    .map((x: any) => x.agencyId);
                let filterableProducts = this.agencyList.filter((agency) => {
                    let isAllowed = allowedProducts.includes(agency.application);
                    let agencyId = agency.id?.split("_")[0];
                    return isAllowed && schedulerAgencies.includes(agencyId);
                });
                filterableProducts?.forEach((agency, index) => {
                    let appName = ApplicationNameEnum[agency.application];

                    if (!this.agencyMultiSelectOptions[appName]) {
                        this.agencyMultiSelectOptions[appName] = [];
                    }

                    this.agencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id?.split("_")[0]
                    });
                    this.agencyOptions.push({
                        name: agency.name,
                        value: agency.id?.split("_")[0]
                    });
                });
                this.selectedAgency = this.agencyOptions[0].value;

                // uncomment once BE accept multiple agencies
                // Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                //     let values = agency.map((item: any) => item.value);
                //     this.selectedAgencies.push(...values);
                // });
            } catch (e) {
                console.warn(e);
            }
        },
        async setBranchMultiSelect() {
            try {
                this.branchMultiSelectOptions = {};
                this.selectedBranches = [];
                if (!this.branchList || this.branchList?.length === 0) {
                    return;
                }
                let allowedProducts = [this.application];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );

                // const selectedAgencies = this.selectedAgencies.map((agency: any) => agency.split("_")[0]);

                let schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        return allowedProducts.includes(application) && this.selectedAgency === item.agencyId;
                    })
                    .map((x: any) => x.agencyId);
                let filterableProducts = this.branchList.filter((branch) => {
                    let isAllowed = allowedProducts.includes(branch.application);
                    return isAllowed && schedulerAgencies.includes(branch.accountId);
                });
                filterableProducts?.forEach((branch) => {
                    let application = branch.application;
                    if (application === ApplicationEnum.AxxessHospiceFE) {
                        application = ApplicationEnum.AxxessHospice;
                    }
                    let appName = ApplicationNameEnum[application];

                    if (!this.branchMultiSelectOptions[appName]) {
                        this.branchMultiSelectOptions[appName] = [];
                    }

                    this.branchMultiSelectOptions[appName].push({
                        displayText: branch.name,
                        value: branch.id
                    });
                });

                Object.values(this.branchMultiSelectOptions).forEach((branch: any) => {
                    let values = branch.map((item: any) => item.value);
                    this.selectedBranches.push(...values);
                });
            } catch (e) {
                console.warn(e);
            }
        },
        async frequencyStatusesFetch(filter: string) {
            const frequencyStatusesEnums = Array.from(FrequencyStatuses.enumWithDescriptions.values()).filter(
                (status: any) => status.value !== 2
            );
            const uniqueOptions = new Map();
            frequencyStatusesEnums.forEach((status: any) => {
                if (!uniqueOptions.has(status.name)) {
                    uniqueOptions.set(status.name, {
                        name: status.name,
                        value: status.value
                    });
                }
            });
            const result = Array.from(uniqueOptions.values()).filter(
                (x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
            return result;
        },
        async frequencyDisciplineFetch(filter: string) {
            const frequencyDisciplineEnums = Array.from(FrequencyHHDiscipline.enumWithDescriptions.values());
            const uniqueOptions = new Map();
            frequencyDisciplineEnums.forEach((status: any) => {
                if (!uniqueOptions.has(status.name)) {
                    uniqueOptions.set(status.name, {
                        name: status.name,
                        value: status.value
                    });
                }
            });
            const result = Array.from(uniqueOptions.values()).filter(
                (x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
            return result;
        },
        selectedStatusesUpdated(options: ITypeaheadOptions[]) {
            this.selectedStatuses = options;
            this.pageReset();
        },
        generate() {
            this.pageReset();
        },
        clearFilters() {
            this.frequencyVariance = null;
            this.selectedStatuses = [];
            this.selectedDiscipline = [];
            this.setAgencyMultiSelect();
            this.setBranchMultiSelect();
            this.dateFrom = moment().subtract(30, "days").format("MM/DD/YYYY") as string;
            this.dateTo = moment().add(30, "days").format("MM/DD/YYYY") as string;
            this.newDateRange = `${this.dateFrom} - ${this.dateTo}`;
            this.pageReset();
        },
        async pageReset() {
            this.isError = false;
            this.freqyencyWatchHHPagination?.reset();
        },
        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            let variance;
            if (this.frequencyVariance == "0") {
                variance = true;
            } else if (this.frequencyVariance == "1") {
                variance = false;
            } else {
                variance = null;
            }
            if (this.selectedBranches.length == 0) {
                this._toastrService.error({
                    title: `Error`,
                    message: `Select at least one agency and branch to proceed.`
                });
                return PAGE_FAIL_RESPONSE;
            }
            try {
                this.isLoading = true;
                this.isError = false;
                const disiplineIds = this.selectedDiscipline.map((option) => {
                    return option.value;
                });
                const statuses = this.selectedStatuses.map((option) => option.value);
                let payload: IFrequencyWatchRequest = {
                    Application: this.application,
                    AccountId: this.selectedAgency,
                    UserId: "",
                    LocationIds: this.selectedBranches,
                    StartDate: this.dateFrom,
                    EndDate: this.dateTo,
                    Page: pageNumber,
                    PageLength: pageSize,
                    SortBy: this.sortBy,
                    Order: this.sortOrder,
                    FrequencyStatuses: statuses
                };
                let response = await this.getFrequencyWatchData(payload, this.application);
                this.page = response.data;

                return {
                    totalPageCount: response.data.PageCount,
                    success: response.data.ItemCount > 0,
                    totalCount: response.data.ItemCount
                };
            } catch (e) {
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },
        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },
        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.pageReset();
            }
        },
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.pageReset();
            }
        },
        updateBranches(branches: any) {
            this.selectedBranches = [];
            this.selectedBranches.push(...branches);
            this.pageReset();
        },

        updateBranchesbyAgency() {
            this.selectedAgenciesName = this.agencyList
                .filter((agency) => this.selectedAgency === agency.id)
                .map((agency) => agency.name);

            const filteredBranches = this.branches.filter((branch) => {
                return branch.application == this.application && this.selectedAgency === branch.accountId;
            });

            this.branchList = filteredBranches;
            this.setBranchMultiSelect();
            this.pageReset();
        },
        selectedItemsUpdated(options: ITypeaheadOptions[]) {
            this.selectedDiscipline = options;
            this.pageReset();
        },
        setnewTaskDateRange(value: any) {
            this.dateFrom = String(value.split("-")[0]).trim();
            this.dateTo = String(value.split("-")[1]).trim();
            this.newDateRange = value;
        }
    }
});
